import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { BPProfilesService, BPProfilesViewCode } from '../../bp-profiles.service';
import { BPProfilesCatalogDataService, CATALOG_ACTION_TYPE_NEW, IOrderableItem, ServiceCatalogType } from './bp-profiles-catalog-data.service';
import { CellClassParams, ColDef, GetRowIdParams, GridOptions, ICellRendererParams, ITooltipParams, ValueFormatterParams } from 'ag-grid-community';
import { UntypedFormGroup } from '@angular/forms';
import { GaFormlyService, IExtraFieldConfigurationObject } from '@app/shared/formly/ga-formly.service';
import { ILov, IObject } from '@type_definitions/datatypes';
import { AgGridDomLayout, GaAgGridService } from '@app/shared/ag-grid-components/ga-ag-grid.service';
import { AuthService } from '@app/shared/services/auth/auth.service';
import { IColDef } from '@app/shared/report/report-grid.service';
import _ from 'underscore';
import { TranslationService } from '@app/shared/translation/translation.service';
import { CellRendererComp } from '@app/shared/ag-grid-components/ga-ag-grid.component';
import { IAgButtonDropdownRendererParams } from '@app/shared/ag-grid-components/ag-button-dropdown/ag-button-dropdown-renderer.component';
import { FormattingService } from '@app/shared/services/formatting.service';
import { util } from '@app/shared/util';
import { DialogBusyButtonFn, DialogSize, GaMessageService, IDialog, IDialogButton } from '@app/shared/message/ga-message.service';
import { BUTTON_CANCEL, BUTTON_DELETE } from '@app/shared/message/dialogs/default-form-dialog.component';
import { StateService, UiOnParamsChanged } from '@uirouter/angular';
import { STATE_BPPROFILES_CATALOG_DETAIL } from '@app/shared/app-states';
import { BPProfilesCatalogService } from './bp-profiles-catalog.service';
import { ScrollService } from '@app/shared/scroll/scroll.service';
import { IRichTextGrid, IRichTextRendererParams, RichTextType } from '@app/shared/ag-grid-components/ag-rich-text/ag-rich-text-renderer.component';
import { Subscription } from 'rxjs';
import { GRID_BP_CATALOG_OVERVIEW } from '@app/shared/app-grids';

enum GridColumn {
  OPTIONS = 'options',
  CATEGORY = 'category',
  OEM = 'oem',
  DESCRIPTION = 'description',
  TYPE = 'type',
  PRICE = 'price',
  UNIT = 'unit'
}

@Component({
  selector: 'bp-profiles-service-catalog-overview',
  template: `
    <button class="btn btn-primary add-catalog-item" *ngIf="hasWritePermission" translate [translateParams]="{ label: ('BPPROFILES.CATALOG.NEW_ITEM' | translate) }" (click)="jumpToAddEdit('${CATALOG_ACTION_TYPE_NEW}')">GA.BUTTONS.ADD_LABEL</button>
    
    <div class="d-block margin-top-sm">
      <formly [form]="filterForm" [fields]="filterFields" [model]="filterModel"></formly>  
    </div>

    <div class="d-block clear margin-top-sm margin-bottom-sm">
      <ga-ag-grid [gridId]="gridId" [gridOptions]="gridOptions" [gridInit]="gridInit"></ga-ag-grid>
    </div>
  `
})
export class BPProfilesCatalogOverviewComponent implements OnInit, OnDestroy, UiOnParamsChanged {
  @Input() public selectedCategory:string;

  public gridOptions:GridOptions;
  public gridInit:boolean;
  public gridId:string = GRID_BP_CATALOG_OVERVIEW;
  public filterForm:UntypedFormGroup = new UntypedFormGroup({});
  public filterFields:IExtraFieldConfigurationObject[];
  public filterModel:IObject = {};
  public hasWritePermission:boolean;

  private possibleCategories:string[];
  private possibleOEMs:string[];
  private categoriesGroupField:IExtraFieldConfigurationObject;
  private valueChangesSubscription:Subscription;

  constructor(
    private formlyService:GaFormlyService,
    private formattingService:FormattingService,
    private bpProfilesService:BPProfilesService,
    private bpProfilesCatalogDataService:BPProfilesCatalogDataService,
    private gaAgGridService:GaAgGridService,
    private authService:AuthService,
    private translationService:TranslationService,
    private messageService:GaMessageService,
    private stateService:StateService,
    private bpProfilesCatalogService:BPProfilesCatalogService,
    private scrollService:ScrollService
  ) {
    /**/
  }

  public ngOnInit():void {
    this.hasWritePermission = this.authService.viewWrite(BPProfilesViewCode.CATALOG);

    // initialize grid
    this.gridInitialize();

    // listen
    this.listen();
  }

  private listen():void {
    this.valueChangesSubscription = this.filterForm.valueChanges.subscribe(() => {
      this.bpProfilesCatalogService.filters = _.clone(this.filterModel);
    });
  }

  public uiOnParamsChanged(newParams:IObject):void {
    const updatedCategoryModel:IObject = {};
    if (!newParams?.category) {
      _.each(this.filterModel, (_value:boolean|string, key:string) => {
        if (key.indexOf('category') > -1) {
          updatedCategoryModel[key] = true;
        }
      });
    } else {
      const keyField:string = `category-${newParams.category.toUpperCase()}`;
      if (this.isFieldKeyExist(keyField)) {
        _.each(this.filterModel, (_value:boolean|string, key:string) => {
          if (key.indexOf('category') > -1) {
            updatedCategoryModel[key] = false;
          }
        });
        updatedCategoryModel[keyField] = true;
      }
    }
    // update formly fields value
    this.filterForm.patchValue(updatedCategoryModel);
  }

  public jumpToAddEdit(catalogItemId?:number|string):void {
    this.stateService.go(STATE_BPPROFILES_CATALOG_DETAIL, { catalogItemId });
  }

  private isFieldKeyExist(key:string):boolean {
    return !!_.findWhere(this.categoriesGroupField.data.fields, { key });
  }

  private createColDefs():ColDef[] {
    const colDefs:IColDef[] = [
      {
        field: GridColumn.OPTIONS,
        headerName: '',
        sortable: false,
        pinned: 'left',
        width: 46,
        suppressHeaderMenuButton: true,
        suppressAutoSize: true,
        suppressMovable: true,
        suppressColumnsToolPanel: true,
        cellClass: 'cell-actions text-center ag-button-dropdown-renderer',
        cellRenderer: CellRendererComp.BUTTON_DROPDOWN_RENDERER,
        cellRendererParams: <Partial<IAgButtonDropdownRendererParams>>{
          dropdownMenus: [
            {
              label: 'GA.BUTTONS.EDIT',
              onClick: this.onEditCatalogItem,
              iconClass: 'fa-pencil'
            },
            {
              label: 'GA.BUTTONS.DELETE',
              onClick: this.onDeleteCatalogItem,
              iconClass: 'fa-trash-o'
            }
          ],
          dropdownContainer: 'body'
        },
        hide: !this.hasWritePermission // hide column when user doesn't have 'write' permission
      },
      {
        field: GridColumn.CATEGORY,
        headerName: 'BPPROFILES.CATALOG.CATEGORY.LABEL',
        valueGetter: 'data.category && data.category.name',
        cellClassRules: {
          'text-muted': (params:CellClassParams) => params.data?.type?.id === ServiceCatalogType.NOT_AVAILABLE
        },
        sortable: true,
        tooltipComponentParams: {
          message: (params:ITooltipParams) => params.node.data?.category?.name
        }
      },
      {
        field: GridColumn.OEM,
        headerName: 'BPPROFILES.CATALOG.OEM.LABEL',
        valueGetter: 'data.oem && data.oem.name',
        cellClassRules: {
          'text-muted': (params:CellClassParams) => params.data?.type?.id === ServiceCatalogType.NOT_AVAILABLE
        },
        sortable: true,
        tooltipComponentParams: {
          message: (params:ITooltipParams) => params.node.data?.oem?.name
        }
      },
      {
        field: GridColumn.DESCRIPTION,
        headerName: 'BPPROFILES.CATALOG.DESCRIPTION.LABEL',
        valueGetter: 'data.shortDescription',
        maxWidth: 600,
        cellClassRules: {
          'text-muted': (params:CellClassParams) => params.data?.type?.id === ServiceCatalogType.NOT_AVAILABLE
        },
        sortable: true,
        tooltipComponentParams: {
          message: (params:ITooltipParams) => params.node.data?.shortDescription
        }
      },
      {
        field: GridColumn.TYPE,
        headerName: 'BPPROFILES.CATALOG.GRID_TYPE',
        valueGetter: 'data.type && data.type.value',
        cellRenderer: CellRendererComp.RICH_TEXT_RENDERER,
        cellRendererParams: <IRichTextRendererParams>{
          type: RichTextType.TEXT,
          getValues: (params:IRichTextRendererParams):IRichTextGrid[] => [
            {
              label: params.value,
              textClass: (params.data?.type?.id === ServiceCatalogType.NOT_AVAILABLE ? 'badge bg-primary' : undefined)
            }
          ]
        },
        sortable: true,
        tooltipComponentParams: {
          message: (params:ITooltipParams) => params.node.data?.type?.value
        }
      },
      {
        field: GridColumn.PRICE,
        headerName: 'BPPROFILES.CATALOG.PRICE.LABEL',
        valueGetter: 'data.price',
        valueFormatter: (params:ValueFormatterParams) => this.formattingService.formatAmount(params.value, this.bpProfilesCatalogService.currency),
        cellRendererSelector: (params:ICellRendererParams) => {
          if (util.isNullOrUndefinedOrEmpty(params.value) && this.hasWritePermission && params.data?.type?.id !== ServiceCatalogType.NOT_AVAILABLE) {
            return {
              component: CellRendererComp.BUTTON_RENDERER,
              params: {
                label: this.translationService.instant('GA.BUTTONS.ADD_LABEL', { label: 'BPPROFILES.CATALOG.PRICE.LABEL' }),
                onClick: this.onEditCatalogItem,
                className: 'btn-primary btn-add-price'
              }
            };
          }
        },
        cellClassRules: {
          'text-end': (params:CellClassParams) => util.notNullOrUndefinedOrEmpty(params.value),
          'text-center': (params:CellClassParams) => util.isNullOrUndefinedOrEmpty(params.value),
          'text-muted': (params:CellClassParams) => params.data?.type?.id === ServiceCatalogType.NOT_AVAILABLE
        },
        sortable: true,
        tooltipComponentParams: {
          message: (params:ITooltipParams) => params.valueFormatted
        }
      },
      {
        field: GridColumn.UNIT,
        headerName: 'BPPROFILES.CATALOG.UNIT.LABEL',
        valueGetter: 'data.unit && data.unit.description',
        cellClass: 'text-center',
        cellClassRules: {
          'text-muted': (params:CellClassParams) => params.data?.type?.id === ServiceCatalogType.NOT_AVAILABLE
        },
        sortable: true,
        tooltipComponentParams: {
          message: (params:ITooltipParams) => params.node.data?.unit?.description
        }
      }
    ];

    _.forEach(colDefs, (colDef:IColDef) => {
      colDef.headerName = this.translationService.instant(colDef.headerName);
      if (colDef.field !== GridColumn.OPTIONS) {
        colDef.resizable = true;
        colDef.tooltipField = colDef.field;
        colDef.tooltipComponent = CellRendererComp.TOOLTIP;
      }
    });

    return colDefs;
  }

  private gridInitialize():void {
    this.gridOptions = {
      columnDefs: this.createColDefs(),
      onGridReady: this.onGridReady,
      onRowDataUpdated: this.onRowDataUpdated,
      onFirstDataRendered: ():void => {
        this.gaAgGridService.sizeColumnsToFit(this.gridId);
      },
      domLayout: AgGridDomLayout.AUTO_HEIGHT,
      getRowId: (row:GetRowIdParams<IOrderableItem>) => row.data.id?.toString(),
      tooltipShowDelay: 0
    };
    this.gridInit = true;
  }

  private onGridReady = ():void => {
    if (this.bpProfilesCatalogService.orderableItems) {
      this.possibleOEMs = util.selectUniqueProperties(this.bpProfilesCatalogService.orderableItems, 'oem.name').sort();
      this.possibleCategories = util.selectUniqueProperties(this.bpProfilesCatalogDataService.getPossibleCategories(), 'description').sort();
      // initialize filters and then refresh grid rows data
      this.initFilter()
        .then(() => {
          this.refreshGridRowsData();
        });
    }
  };

  private onRowDataUpdated = ():void => {
    // if 'highlightedId' is available then highlight the grid row and scroll to this row
    if (this.bpProfilesCatalogService.highlightedId) {
      // add timeout to let ag-grid process all data in the grid
      setTimeout(() => {
        this.gaAgGridService.highlightGridRow(this.gridId, this.bpProfilesCatalogService.highlightedId);
        this.scrollService.scrollToElement(`div[row-id=${this.bpProfilesCatalogService.highlightedId}]`);
        // after row has been highlighted then clear the stored data
        this.bpProfilesCatalogService.highlightedId = undefined;
      });
    }
  };

  private refreshGridRowsData = ():void => {
    const selectedCategories:string[] = _.map(this.filterModel, (value:boolean, key:string) => {
      if (key.indexOf('category') > -1 && value) {
        return key.split('-')[1];
      }
    });

    const selectedTypes:string[] = _.map(this.filterModel, (value:boolean, key:string) => {
      if (key.indexOf('type') > -1 && value) {
        return key.split('-')[1];
      }
    });

    this.gaAgGridService.setRowData(this.gridId,
      _.chain(this.bpProfilesCatalogService.orderableItems)
        .filter((orderableItem:IOrderableItem) => util.notNullOrUndefinedOrEmpty(this.filterModel.oem) ? orderableItem.oem?.name === this.filterModel.oem : true)
        .filter((orderableItem:IOrderableItem) => _.indexOf(selectedCategories, orderableItem.category?.description) > -1)
        .filter((orderableItem:IOrderableItem) => _.indexOf(selectedTypes, orderableItem.type?.id?.toString()) > -1)
        .value()
    );
  };

  private initFilter():Promise<void> {
    if (this.bpProfilesCatalogService.filters) {
      this.filterModel = this.bpProfilesCatalogService.filters;
    } else {
      // init filter model
      _.each(this.bpProfilesCatalogService.catalogTypes, (type:ILov) => {
        const id:string = type.id.toString();
        // turn on all filters except 'not available'
        if (id !== ServiceCatalogType.NOT_AVAILABLE) {
          this.filterModel[`type-${id}`] = true;
        }
      });

      _.each(this.possibleCategories, (category:string) => {
        // init all category checkboxes to false
        this.filterModel[`category-${category}`] = false;
        // init model of category
        if (this.selectedCategory) {
          this.filterModel[`category-${this.selectedCategory.toUpperCase()}`] = true;
        } else if (!this.bpProfilesCatalogService.filters) {
          this.filterModel[`category-${category}`] = true;
        }
      });
    }

    // create filter fields
    this.filterFields = this.createFilterFields();

    return Promise.resolve();
  }

  private createFilterFields():IExtraFieldConfigurationObject[] {
    const halfWidthClass:string = 'col-sm-6';
    const checkboxClass:string = 'col-auto text-nowrap';

    return [
      this.formlyService.aGroup()
        .withFields([
          this.formlyService.aGroup()
            .withoutWrapper()
            .withWrapperClass('col-sm-8')
            .withFields([
              this.formlyService.aSelect2('oem')
                .withOptions(this.possibleOEMs)
                .withoutWrapper()
                .withWrapperClass(halfWidthClass)
                .withExtraOptions({
                  templateOptions: {
                    noTrack: true,
                    allowClear: true
                  }
                })
                .onChange(this.refreshGridRowsData)
                .build(),
              this.formlyService.aGroup('catalogTypeGroup')
                .withFields(
                  _.map(this.bpProfilesCatalogService.catalogTypes, (catalogType:ILov) => {
                    return this.formlyService.aCheckbox(`type-${catalogType.id.toString()}`, catalogType.value)
                      .withoutWrapper()
                      .withWrapperClass(checkboxClass)
                      .onChange(this.refreshGridRowsData)
                      .build();
                  })
                )
                .withoutWrapper()
                .withWrapperClass(halfWidthClass)
                .build()
            ])
            .build(),
          this.categoriesGroupField = this.formlyService.aGroup('categoriesGroup')
            .withFields(
              _.map(this.possibleCategories, (category:string) => {
                return this.formlyService.aCheckbox(`category-${category}`, category)
                  .withoutWrapper()
                  .withWrapperClass(checkboxClass)
                  .onChange(this.refreshGridRowsData)
                  .build();
              })
            )
            .withoutWrapper()
            .withWrapperClass('col-sm-4')
            .build()
        ])
        .withoutWrapper()
        .build()
    ];
  }

  private onEditCatalogItem = (params:IAgButtonDropdownRendererParams):void => {
    this.jumpToAddEdit((<IOrderableItem>params.data).id);
  };

  private onDeleteCatalogItem = (params:IAgButtonDropdownRendererParams):void => {
    const dialogButtons:IDialogButton[] = [
      {
        id: BUTTON_DELETE,
        label: 'GA.BUTTONS.DELETE',
        onClick: (updateBusyButtonStatus:DialogBusyButtonFn) => {
          updateBusyButtonStatus(true);
          this.bpProfilesCatalogDataService.deleteCatalogItem(this.bpProfilesService.activeProfile?.id, params.data?.id)
            .then(() => {
              const dataIndex:number = _.findIndex(this.bpProfilesCatalogService.orderableItems, { id: params.data?.id });
              // mark as not available
              this.bpProfilesCatalogService.orderableItems[dataIndex].type = _.findWhere(this.bpProfilesCatalogService.catalogTypes, { id: ServiceCatalogType.NOT_AVAILABLE });
              this.refreshGridRowsData();
              dialog.hide();
            })
            .finally(() => {
              updateBusyButtonStatus(false);
            });
        },
        className: 'btn-danger'
      },
      { id: BUTTON_CANCEL, label: this.translationService.instant('GA.BUTTONS.DONT_LABEL', { label: 'GA.BUTTONS.DELETE' }), onClick: () => dialog.hide(), className: 'btn-success' }
    ];

    const dialog:IDialog = this.messageService.showMessageDialog(
      'GA.GENERAL.CONFIRM_DELETE',
      this.translationService.instant('BPPROFILES.CATALOG.CONFIRM_DELETE_ITEM', { description: (<IOrderableItem>params.data)?.shortDescription }),
      dialogButtons,
      undefined,
      undefined,
      DialogSize.MD
    );
  };

  public ngOnDestroy():void {
    this.valueChangesSubscription?.unsubscribe();
  }
}